/*
 * Normalizers are used by the API middleware to take responses and convert them be used in our
 * Redux state tree. Part of the work is converting the taxonomy the API uses (project, collection)
 * to the user-facing taxonomy (kit, group). The more important aspect is normalizing the nested
 * objects into a flat representation, so that the entities can be shared without duplicating data.
 */

import { TinyColor } from "@ctrl/tinycolor";
import { AssetType, ItemType } from "../constants/types";
import { slugifyName } from "../helpers/slugify";

import {
  AnyObject,
  AccountCharge,
  APIToken,
  Space,
  SpaceJoinSettings,
  SpaceSubscription,
  SpaceTheme,
  SSOOrganization,
  SpaceMember,
  Kit,
  KitVersion,
  KitMember,
  User,
  Invitation,
  KitAccess,
  Item,
  Asset,
  Color,
  CustomField,
  SpacePreview,
  Portal,
  PortalItem,
  PortalMember,
  ThemeHeaderTitleOptions,
  Product,
} from "../models";
import { AssetView, Section } from "..";

import camelize from "../helpers/camelize";

// Helper to cast unknown to Record<string, unknown>
function asObj(val: unknown): Record<string, unknown> {
  return val as Record<string, unknown>;
}
// Helper to cast unknown to array
function asArr(val: unknown, def = []): unknown[] {
  return Array.isArray(val) ? val : def;
}

function isArray<T>(val: unknown): val is T[] {
  return val && Array.isArray(val);
}

export function camelCaseKeys(object: AnyObject): AnyObject {
  const snakeToCamel = (str: string) =>
    str.replace(/([-_][a-z0-9])/g, group => group.toUpperCase().replace("-", "").replace("_", ""));
  return Object.keys(object).reduce((acc, key) => {
    acc[key.startsWith("__") ? key : snakeToCamel(key)] = object[key];
    return acc;
  }, {});
}

export function formatUser(data: unknown): User {
  return camelCaseKeys(data as AnyObject) as User;
}

// MARK : Space
/*-------------------------------------------------------------------------------*/
export function formatSpace(data: unknown): Space {
  const d = asObj(data);
  if (d.__formatted) {
    return d as Space;
  }

  const res = camelCaseKeys(d);
  res.__formatted = true;
  const avatars = asObj(d.avatars);
  res.avatar = avatars.avatar || avatars.default_avatar;
  res.avatars = camelize(avatars);
  res.fields = asArr(d.fields).map(formatCustomField);
  res.joinSettings = d.join_settings ? formatSpaceJoinSettings(d.join_settings) : null;
  res.access = d.access ? formatSpaceMember(d.access) : {};
  res.kits = asArr(d.kits).map(formatKit);
  res.theme = d.theme ? formatTheme(d.theme) : undefined;
  res.account = formatSpaceAccount(d);
  delete res.charges;
  delete res.card;
  delete res.balance;
  res.ssoOrganization = d.sso_organization ? formatSsoOrganization(d.sso_organization) : null;
  res.subscription = d.subscription ? formatSubscription(d.subscription) : null;
  res.plan = d.plan ? camelize(d.plan as AnyObject) : null;
  return res as Space;
}

function formatSpaceJoinSettings(data: AnyObject): SpaceJoinSettings {
  // join_with, allowed_domains, token
  return camelCaseKeys(data) as SpaceJoinSettings;
}

export function formatSpacePreview(data: unknown): SpacePreview {
  if (!data) return null;
  const d = asObj(data);
  if (d.__formatted) {
    return d as SpacePreview;
  }

  function formatPreviewMember(m: AnyObject) {
    return {
      id: m.id,
      name: m.name,
      email: m.email,
      avatar: m.avatar,
      role: m.role,
    };
  }

  return {
    __formatted: true,
    // id, name, subdomain, avatar, avatars, name, ssoOrganization, counts
    ...(camelize(d) as AnyObject),
    theme: formatTheme(d.theme),
    members: (d.members as AnyObject[])?.map(formatPreviewMember),
  } as unknown as SpacePreview;
}

function formatSsoOrganization(data: AnyObject): SSOOrganization {
  return camelCaseKeys(data) as SSOOrganization;
}

function formatTheme(data: unknown): SpaceTheme {
  if (!data) return null;

  const converted = camelCaseKeys(data);
  if (converted.headerTitleStyle === "space_name") {
    converted.headerTitleStyle = ThemeHeaderTitleOptions.name;
  }
  return {
    // default
    headerBackgroundColor: "#FAFAFA",
    ...converted,
  } as SpaceTheme;
}

function formatSubscription(data: AnyObject): SpaceSubscription {
  return camelize(data) as SpaceSubscription;
}

function formatSpaceAccount(data: AnyObject) {
  return {
    balance: data.balance,
    card: data.card && formatCard(data.card as AnyObject),
    charges: isArray(data.charges) ? data.charges.map(formatCharge) : null,
  };
}

function formatCard(data: AnyObject) {
  return camelCaseKeys(data);
}

export function formatCharge(data: unknown): AccountCharge {
  return camelCaseKeys(data) as AccountCharge;
}

// MARK : Space Member
/*-------------------------------------------------------------------------------*/

export function formatSpaceMember(data: unknown): SpaceMember {
  if (!data) return data as SpaceMember;
  const d = asObj(data);
  if (d.__formatted) {
    return d as SpaceMember;
  }
  const kits = asArr(d.kits)?.map(formatKit);
  const kitCount =
    d.joined_kits || kits?.reduce((res, k) => res + (k.access?.status === "active" ? 1 : 0), 0);
  const kitAccess = asArr(d.kit_access, null)?.map(formatKitMember);
  const portalAccess = asArr(d.portal_access, null)?.map(formatPortalMember);
  return {
    __formatted: true,
    ...camelCaseKeys(d),
    id: d.id,
    role: d.role,
    counts: kits,
    status: d.status,
    kitAccess,
    portalAccess,
    isPublic: Boolean(d.user_id) === false || d.role === "guest",
    userId: d.user_id,
    spaceId: d.space_id,
    joinedKits: kitCount,
    user: d.user ? formatUser(d.user) : undefined,
    notificationPrefs: d.notification_prefs ? camelCaseKeys(d.notification_prefs) : {},
    permissions: d.permissions || [],
    ssoEnabled: d.sso_enabled,
    dateAdded: d.date_added,
    dateUpdated: d.date_updated,
    isEditor: d.is_editor,
  } as SpaceMember;
}

// MARK : API Token
/*-------------------------------------------------------------------------------*/

export function formatApiToken(data: AnyObject): APIToken {
  return camelCaseKeys(data) as APIToken;
}

// MARK : Portal
/*-------------------------------------------------------------------------------*/

export function formatPortal(data: unknown): Portal {
  if (!data) return null;
  const d = asObj(data);

  const res = camelize(d) as Portal;
  if (res.access) {
    res.access.isPublic = Boolean((res.access as AnyObject).userId) === false;
  }
  res.space = res.space ? formatSpace(res.space) : undefined;
  res.urlId = slugifyName(res.name as string, res.shortId as string);
  return res;
}

// MARK : Portal Member
// -------------------------------------------------------------------------------

export function formatPortalMember(data: unknown): PortalMember {
  if (!data) return data as PortalMember;
  const obj = asObj(data);
  const d = camelize(obj) as Record<string, unknown>;
  return {
    ...d,
    user: d.user ? formatUser(d.user) : undefined,
    portal: d.portal ? formatPortal(d.portal) : undefined,
    permissions: d.permissions || [],
    isPublic: Boolean(d.userId) === false,
  } as unknown as PortalMember;
}

// MARK : Portal Item
/*-------------------------------------------------------------------------------*/

export function formatPortalItem(data: AnyObject): PortalItem {
  if (!data) return null;
  const res = camelize(data) as PortalItem;
  res.kit = data.kit && formatKit(data.kit);
  res.kitId = res.kit?.kitId;
  res.asset = data.asset && formatAsset(data.asset);

  if (res.data) {
    res.data.backgroundColor = res.data.backgroundColor || "default";
  }
  return res;
}

// MARK : Kit
/*-------------------------------------------------------------------------------*/

export function formatKit(data: unknown): Kit {
  const d = asObj(data);
  if (d.__formatted) {
    return d as Kit;
  }
  const access = asObj(d.access);
  const userVersion = access?.use_version;
  const recommendedVersion = d.use_version;
  return {
    __formatted: true,
    ...camelize(d),
    kitId: d.kit_uuid,
    shortId: d.short_id,
    urlId: slugifyName(d.name as string, d.short_id as string),
    dateAdded: d.date_added,
    dateUpdated: d.date_updated,
    name: d.name,
    description: d.description,
    status: d.status,
    portalAccess: d.portal_access,
    portals: asArr(d.portals, null)?.map(formatPortal),
    images: d.images,
    counts: d.counts || {},
    privacy: d.privacy,
    indexable: d.indexable,
    shareToken: d.token,
    recommendedVersion: recommendedVersion,
    userVersion: userVersion === null ? recommendedVersion : userVersion,
    access: d.access ? formatKitMember(d.access) : {},
    theme: d.theme ? (camelCaseKeys(d.theme) as Kit["theme"]) : null,
    spaceId: d.space_id,
    space: d.space ? formatSpace(d.space) : null,
    versions: asArr(d.versions, null)?.map(formatKitVersion),
    members: asArr(d.members).map(formatKitMember),
    purchase: d.purchase ? (camelCaseKeys(d.purchase) as Kit["purchase"]) : null,
    publisher: d.publisher ? (camelCaseKeys(d.publisher) as Kit["publisher"]) : null,
    downloadsRestricted: d.downloads_restricted || false,
    downloadsRestrictedApproveMode: d.downloads_restricted_approve_mode,
  } as unknown as Kit;
}

// MARK : Kit Member
/*-------------------------------------------------------------------------------*/

export function formatKitMember(data: AnyObject): KitMember | KitAccess {
  if (!data) return null;
  if (data.result) data = asObj(data.result).member;
  if (data.__formatted) {
    return data as KitMember | KitAccess;
  }
  return {
    __formatted: true,
    ...camelCaseKeys(data),
    user: data.user ? formatUser(data.user) : null,
    permissions: data.permissions || [],
    isFavorite: Boolean(data.is_favorite),
    isPublic: Boolean(data.user_id) === false,
    kitId: data.kit_uuid,
    kit: data.kit && formatKit(data.kit),
  } as unknown as KitMember | KitAccess;
}

// MARK : Invitation
/*-------------------------------------------------------------------------------*/

export function formatInvitation(json: unknown): Invitation {
  const data = asObj(json);
  if (!data) return data as Invitation;
  if (data.result) return camelize(data.result as AnyObject) as Invitation;
  return camelize(data) as Invitation;
}

// MARK : Kit Version
/*-------------------------------------------------------------------------------*/

export function formatKitVersion(data: unknown): KitVersion {
  const d = asObj(data);
  if (d.__formatted) {
    return data as KitVersion;
  }
  return {
    __type: d.__type,
    __formatted: true,
    version: d.version,
    kitId: d.kit_uuid,
    versionIdentifier: d.version_identifier,
    notes: d.notes,
    creator: d.creator ? formatUser(d.creator) : null,
    dateAdded: d.date_added,
    dateUpdated: d.date_updated,
    spaceId: d.space_id,
    status: d.status,
    counts: d.counts,
    sections: asArr(d.sections, null)?.map(s => {
      return { ...formatSection(s), kitId: d.kit_uuid };
    }) as unknown as string[], // returning normalized but model expects denormalized
  } as KitVersion;
}

// MARK : Section
/*-------------------------------------------------------------------------------*/

export function formatSection(data: unknown): Section {
  const d = asObj(data);
  if (d.__formatted) {
    return d as Section;
  }
  return {
    __type: d.__type,
    __formatted: true,
    id: d.uuid,
    counts: d.counts,
    version: d.version,
    shortId: d.short_id,
    urlId: slugifyName(d.name as string, d.short_id as string),
    name: d.name,
    status: d.status,
    displayOrder: d.display_order,
    items: asArr(d.items, null)?.map(formatItem),
    kitId: d.kit_uuid,
    headers: asArr(d.headers, null)?.map(formatOutlineHeader),
    isSeparator: Boolean(
      (d.name as string)?.startsWith("---") && (d.counts as Section["counts"])?.items === 0
    ),
  } as unknown as Section;
}

export function formatOutlineHeader(data: AnyObject) {
  return {
    id: data.uuid,
    shortId: data.short_id,
    name: data.name,
    version: data.version,
    displayOrder: data.display_order,
  };
}

// MARK : Saved Views
/*-------------------------------------------------------------------------------*/

export const formatAssetView = (data: AnyObject): AssetView => {
  return camelize(data) as AssetView;
};

// MARK : Custom Fields
/*-------------------------------------------------------------------------------*/

export const formatCustomField = (data: unknown): CustomField => {
  return camelize(data as AnyObject) as CustomField;
};

// MARK : Item
/*-------------------------------------------------------------------------------*/

export function formatItem(data: unknown): Item {
  if (!data) return null;
  const d = asObj(data);
  if (d.__formatted) {
    return d as Item;
  }
  const itemData = formatItemData(d.data);
  const displayProperties = formatDisplayProperties(d.display_properties);
  return {
    __type: d.__type,
    __formatted: true,
    id: d.uuid,
    version: d.version,
    shortId: d.short_id,
    kitId: d.kit_uuid,
    sectionId: d.section_uuid,
    itemId: d.item_uuid,
    urlId: slugifyName(
      (itemData.viewName ?? itemData.name ?? (d.asset as AnyObject)?.name) as string,
      d.short_id as string
    ),
    spaceId: d.space_id,
    assetId: d.asset_uuid,
    displayOrder: d.display_order,
    status: d.status,
    type: d.type,
    data: itemData,
    dateAdded: d.date_added,
    dateUpdated: d.date_updated,
    displayProperties: formatDisplayPropertyDefaults(displayProperties, d),
    space: d.space ? formatSpace(d.space) : null,
    item: d.item ? formatItem(d.item) : null,
    items: asArr(d.items, null)?.map(formatItem),
    kit: d.kit ? formatKit(d.kit) : null,
    section: d.section ? formatSection(d.section) : null,
    kitVersion: d.kit_version ? formatKitVersion(d.kit_version) : null,
    asset: d.asset ? formatAsset(d.asset) : null,
  } as unknown as Item;
}

function formatItemData(data: AnyObject): Item["data"] {
  const res = camelCaseKeys(data);
  res.backgroundColor = res.backgroundColor || "default";
  return res as Item["data"];
}
function formatDisplayProperties(data: AnyObject): Item["displayProperties"] {
  if (!data) return;
  const res = camelCaseKeys(data);
  return res as Item["displayProperties"];
}

function formatDisplayPropertyDefaults(
  dp: Item["displayProperties"] = {},
  rawItem: AnyObject
): Item["displayProperties"] {
  const type = rawItem.type as ItemType;
  const rawData = rawItem.data as AnyObject | undefined;

  const defaults: Partial<Item["displayProperties"]> = {};

  switch (type) {
    case ItemType.asset: {
      const isFullWidth =
        ["leading", "trailing"].includes(dp.imageAlignment) ||
        rawData?.display_style === "full_width";
      defaults.allowDownload = true;
      defaults.showMetadata = true;
      defaults.size = isFullWidth ? 6 : 5;
      defaults.imageAlignment = isFullWidth ? "leading" : "top";
      defaults.cardBackgroundColor = !isFullWidth ? "clear" : "noteNeutralColor";
      break;
    }
    case ItemType.codeSnippet:
      defaults.size = 1;
      defaults.cardBackgroundColor = "noteNeutralColor";
      break;
    case ItemType.note:
    case ItemType.supportingImage:
      defaults.size = 1;
      defaults.cardBackgroundColor = rawData?.background_color;
      defaults.showMetadata = false;
      defaults.allowDownload = false;
      break;
    case ItemType.gallery:
      defaults.size = 2;
      defaults.imageAlignment = "leading";
      defaults.cardBackgroundColor = rawData?.background_color ?? "noteNeutralColor";
      break;
    case ItemType.guide:
      defaults.imageAlignment = "top";
      defaults.size = rawData.display_size > 0 ? 2 : 1;
      defaults.displayStyle = rawData.display_style ?? "image";
      break;
  }

  const result = { ...defaults, ...dp };

  return result;
}
// MARK : Item.Asset
// asset is always contained in an item
/*-------------------------------------------------------------------------------*/

export function formatAsset(_data: unknown): Asset {
  const data = asObj(_data);
  if (!data) return null;
  if (data.__formatted) {
    return data as Asset;
  }
  if (!data.meta) data.meta = {};
  function decacheUrl(url: string) {
    if (typeof url !== "string") return url;
    return url.includes("?") ? `${url}&hash=${data.file_hash}` : `${url}?hash=${data.file_hash}`;
  }

  return {
    __type: data.__type,
    __formatted: true,
    id: data.uuid,
    sourceId: data.source_uuid,
    spaceId: data.space_id,
    name: data.name,
    notes: data.notes,
    keywords: data.keywords,
    autoKeywords: data.auto_keywords,
    type: data.type,
    category: data.category,
    size: data.size,
    shareToken: data.token,
    ...getAssetDimensions(data),
    colors: ((data.colors as unknown[]) || []).map(formatAssetColor),
    isFont: assetIsFont(data),
    permalink: decacheUrl(data.permalink as string),
    thumbnails:
      data.thumbnails &&
      (Object.keys(data.thumbnails).reduce((res, k) => {
        res[k] = decacheUrl(data.thumbnails[k] as string);
        return res;
      }, {}) as Asset["thumbnails"]),
    thumbnailPlaceholders: data.thumbnail_placeholders as Asset["thumbnailPlaceholders"],
    meta: formatAssetMeta(data.meta),
    uploaderId: data.uploader_id,
    uploader: data.uploader && formatUser(data.uploader),
    fields: data.fields,
    versionedFields: asArr(data.versioned_fields, null)?.map(formatCustomField),
    fileId: data.file_id,
    fileHash: data.file_hash,
    fileUpdated: data.file_updated,
    dateAdded: data.date_added,
    dateUpdated: data.date_updated,
    items: asArr(data.items, null)?.map(r => formatItem(r)),
  } as Asset;
}

function formatAssetColor(color: AnyObject): Color {
  const c = new TinyColor(
    `hsva(${color.hue}, ${color.saturation}%, ${color.brightness}%, ${color.alpha / 100})`
  );
  return {
    ...color,
    hex: c.toHexString(),
    hex8: c.toHex8String(),
    rgb: c.toRgbString(),
    hsb: c.toHsvString().replace("hsv", "hsb"),
    name: color.name,
    pms: color.pms || null,
    cmyk: color.cmyk || null,
  } as Color;
}

function formatAssetMeta(meta: AnyObject): Asset["meta"] {
  const { font, textStyle, filecuts, assetProcessing, backgroundColor, ...rest } =
    camelCaseKeys(meta);
  return {
    assetProcessing,
    font: font && formatFontData(font as AnyObject),
    textStyle: textStyle && formatAssetTextStyle(textStyle as AnyObject),
    filecuts: filecuts && formatFilecuts(filecuts as AnyObject),
    backgroundColor,
    ...rest,
  } as Asset["meta"];
}

function formatFilecuts(data: AnyObject): Asset["meta"]["filecuts"] {
  return {
    availableTypes: data.available_types || [],
    presets: data.presets || [],
  };
}

function formatFontData(data: AnyObject): Asset["meta"]["font"] {
  return {
    displayName: data.display_name,
    name: data.font_name,
    family: data.family,
    variant: data.variant,
    googleVariant: data.g_variant,
    extension: data.extension,
    source: data.source,
    stylesheetUrl: data.stylesheet_url,
  };
}

function formatAssetTextStyle(data: AnyObject) {
  return {
    color: data.color || null,
    decoration: data.decoration || null,
    lineHeight: data.line_height || null,
    size: data.size || null,
    transform: data.transform || null,
  };
}

function assetIsFont(a: AnyObject) {
  if (a.type !== AssetType.textStyle) return false;
  const meta = asObj(a.meta);
  return Boolean(meta.text_style && Object.keys(meta.text_style).length);
}

function getAssetDimensions(a: AnyObject) {
  // Convert dimensions to ints (96.0x96.0 -> 96x96)
  const dimensions = a.dimensions as string;
  const comps = dimensions.split("x").map(px => Math.round(parseFloat(px)));

  return {
    dimensions: comps.join("x"),
    width: comps[0],
    height: comps[1],
  };
}

// MARK : Space Search
/*-------------------------------------------------------------------------------*/

// MARK : Products
/*-------------------------------------------------------------------------------*/

export function formatProductType(data: unknown) {
  if (!data) return data;
  const d = asObj(data);
  if (d.__formatted) {
    return d;
  }
  return {
    __formatted: true,
    ...camelCaseKeys(d),
    fields: asArr(d.fields).map(formatField),
  };
}

export function formatField(data: unknown) {
  if (!data) return data;
  const d = asObj(data);
  if (d.__formatted) {
    return d;
  }
  return {
    __formatted: true,
    ...camelCaseKeys(d),
    config: d.config ? camelize(d.config as object) : {},
    options: asArr(d.options, []).map(opt => {
      const o = asObj(opt);
      return {
        uuid: o.uuid,
        value: o.value,
        label: o.label,
        displayOrder: o.display_order,
      };
    }),
  };
}

// MARK : Markets
/*-------------------------------------------------------------------------------*/

export function formatMarket(data: unknown) {
  if (!data) return data;
  const d = asObj(data);
  if (d.__formatted) {
    return d;
  }
  return {
    __formatted: true,
    ...camelCaseKeys(d),
  };
}

// MARK : Products
/*-------------------------------------------------------------------------------*/

export function formatProduct(data: unknown): Product {
  if (!data) return null;
  const d = asObj(data);
  if (d.__formatted) {
    return d as Product;
  }

  const formatted = camelCaseKeys(d);
  return {
    __formatted: true,
    ...formatted,
    counts: camelize(d.counts as AnyObject),
    fields: formatPimFields(d.fields),
    productType: formatProductType(d.product_type),
    previewAsset: d.preview_asset ? formatAsset(d.preview_asset) : null,
    variants: asArr(d.variants, null)?.map(formatVariant),
  } as unknown as Product;
}

export function formatVariant(data: unknown) {
  if (!data) return data;
  const d = asObj(data);
  if (d.__formatted) {
    return d;
  }
  return {
    __formatted: true,
    ...camelCaseKeys(d),
    fields: formatPimFields(d.fields),
  };
}

function formatPimFieldValue(raw: unknown): AnyObject {
  const v = asObj(raw);
  const out: AnyObject = { source: v.source ?? null };
  if ("value" in v) out.value = v.value;
  if ("option" in v) out.option = v.option ?? null;
  if ("options" in v) out.options = asArr(v.options, []);
  if ("assets" in v) {
    out.assets = asArr(v.assets, []).map((entry: unknown) => {
      const a = asObj(entry);
      return {
        assetUuid: a.asset_uuid,
        displayOrder: a.display_order,
        inherited: a.inherited ?? false,
        asset: a.asset ? formatAsset(a.asset) : undefined,
      };
    });
  }
  return out;
}

export function formatPimFields(raw: unknown): Record<string, AnyObject> {
  if (!raw || typeof raw !== "object") return {};
  return Object.fromEntries(
    Object.entries(raw as AnyObject).map(([k, v]) => [k, formatPimFieldValue(v)])
  );
}

// MARK : Presets
/*-------------------------------------------------------------------------------*/

export function formatPreset(data: unknown) {
  if (!data) return data;
  const d = asObj(data);
  if (d.__formatted) {
    return d;
  }
  return {
    __formatted: true,
    ...camelCaseKeys(d),
  };
}

// MARK : Exports
/*-------------------------------------------------------------------------------*/

export const format = {
  item: formatItem,
  asset: formatAsset,
  section: formatSection,
  kit: formatKit,
  user: formatUser,
  kitVersion: formatKitVersion,
  space: formatSpace,
  spacePreview: formatSpacePreview,
  spaceJoinSettings: formatSpaceJoinSettings,
  spaceMember: formatSpaceMember,
  portal: formatPortal,
  portalMember: formatPortalMember,
  portalItem: formatPortalItem,
  kitMember: formatKitMember,
  productType: formatProductType,
  field: formatField,
  market: formatMarket,
  product: formatProduct,
  variant: formatVariant,
};
